<template>
  <div class="form-group" :class="{'error': invalidValue}">
    <div class="d-flex justify-content-between">
      <label :class="{ 'visuallyhidden': !showLabel }">{{ labelName }}</label>
      <slot name="extend-label" />
    </div>
    <div class="input-container">
      <input
        ref="inputElement"
        v-model="mapInputValue"
        :type="currentInputType"
        :name="name"
        class="form-control input-field"
        :placeholder="showPlaceholder ? placeholder : ''"
        :disabled="disabled"
        :tabindex="tabIndex"
        @blur="validateValue"
        @focus="onFocus"
        @keyup.enter.stop="onEnterCallback()"
      >
      <img
        v-if="inputType === 'password'"
        class="toggle-password"
        :src="passwordVisible ? require('@assets/images/eye-blocked.svg') : require('@assets/images/eye.svg')"
        alt="toggle password"
        @mousedown.prevent="togglePasswordVisibility"
      >
    </div>
    <fade-transition>
      <small v-if="showSubText" class="form-text">{{ message }}</small>
    </fade-transition>
  </div>
</template>

<script>
import Debounce from '../../utils/debounce'
import ValidateUtil from '../../utils/validate'

export default {
  name: 'FormInput',
  props: {
    overrideValidation: {
      type: Boolean,
      default: false
    },
    overrideValue: {
      type: Boolean,
      default: true
    },
    initialLoadValue: {
      type: Boolean,
      default: undefined
    },
    labelName: {
      type: String,
      required: true
    },
    inputType: {
      type: String,
      default: 'text'
    },
    name: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    inputValue: {
      required: true
    },
    inputValueWithValidation: {
      default: false
    },
    inputValid: {
      default: false
    },
    validationMessage: {
      default: ''
    },
    helperText: {
      default: ''
    },
    tabIndex: {
      type: Number
    },
    validations: {
      type: Array,
      required: false,
      default: () => ([]),
      validator: (values) => {
        const validationTypes = ValidateUtil.validationTypes()
        const valid = values.every(v => validationTypes.includes(v))
        if (!valid) {
          console.warn(`prop validations contains a type that is not supported. Types supported are: [${validationTypes.join(', ')}], prop validations: [${values.join(', ')}]`)
          return false
        }
        return true
      }
    },
    onEnterCallback: {
      type: Function,
      default: () => {
        console.warn('enter callback was not provided')
      }
    },
    onBlur: {
      type: Function,
      default: () => {}
    },
    onFocus: {
      type: Function,
      default: () => {}
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    showPlaceholder: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    instaFocus: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      initialLoad: true,
      passwordVisible: false,
      validateValueFunc: Debounce.apply(function (value) {
        this.validateValue(value)
      }, 500, this)
    }
  },
  computed: {
    mapInputValue: {
      get () {
        if (typeof this.inputValue === 'object') return this.inputValue.value
        return this.inputValue
      },
      set (val) {
        const value = this.inputValueWithValidation
          ? { value: val, validation: this.inputValue.validate(val) }
          : val

        this.$emit('update:inputValue', value)
        this.$emit('updateInputValue', value)
      }
    },
    invalidValue () {
      if (this.overrideValidation) return !this.overrideValue
      return !(this.loadValue || this.inputValid)
    },
    loadValue () {
      if (typeof this.initialLoadValue !== 'undefined') return this.initialLoadValue
      return this.initialLoad
    },
    message () {
      return this.invalidValue ? this.validationMessage : this.helperText
    },
    showSubText () {
      return typeof this.helperText !== 'undefined' && this.helperText.length > 0 || this.invalidValue
    },
    currentInputType () {
      if (this.inputType === 'password') {
        return this.passwordVisible ? 'text' : 'password'
      }
      return this.inputType
    }
  },
  mounted () {
    if (this.instaFocus && this.$refs.inputElement) {
      this.$refs.inputElement.focus()
    }
  },
  methods: {
    setInitialLoad () {
      this.$emit('inputTouched')

      if (!this.initialLoad) return
      this.initialLoad = false
    },
    // changeValue (e) {
    //   const val = e.target.value
    //   this.$emit('update:inputValue', val)
    //   this.$emit('updateInputValue', val)
    // },
    validateValue (e) {
      this.onBlur()
      if (this.validations.length === 0) return this.setInitialLoad()
      const value = typeof e === 'object' ? e.target.value : e
      const validation = { valid: true, validationMessage: '' }
      for (const validation of this.validations) {
        const v = ValidateUtil.validate(validation, value)
        if (!v.valid) {
          validation.valid = v.valid
          validation.validationMessage = v.message
          break
        }
      }
      this.$emit('update:inputValid', validation)
      this.$emit('updateInputValid', validation)
      this.setInitialLoad()
    },
    togglePasswordVisibility () {
      this.passwordVisible = !this.passwordVisible
    }
  }
}
</script>

<style lang="scss">
.visuallyhidden {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
}

.input-container {
  align-items: center;
  display: flex;

  .input-field {
    padding-right: 2.5rem;
  }

  .toggle-password {
    position: absolute;
    right: 1rem;
    cursor: pointer;
    width: 1.4rem;
  }
}
</style>
